/*
 * The rules by which a table's search and facets narrow its rows and count its facets' values, and
 * which rows it pins first, kept apart from the component so they can be checked without a browser.
 */

/** A facet's values for one row: what the row can be filtered by, each by its `value`. */
export type FacetOf<Row, Value extends { value: string }> = (row: Row) => readonly Value[]

/**
 * The rows a view leaves, whether it narrows them at all, and each facet's values with a count.
 *
 * A row passes the search when its text holds the query, in any case; a blank query passes every
 * row, and a row with no text passes only a blank one. It passes a facet when nothing is chosen
 * there or the row has any chosen value, and the view when it passes the search and every facet.
 *
 * A facet counts the rows its own choice would leave, among those the search and every other facet
 * leave: a value's count is the rows passing everything but that facet that carry the value. Its
 * values are every value any row has, matching or not, so a choice the other filters have
 * narrowed to nothing stays on offer, at 0, to be unchosen.
 */
export function filterRows<Row, Id extends string, Value extends { value: string }>({
  rows,
  facets,
  search,
  matches,
  filters,
  query,
}: {
  rows: readonly Row[]
  facets: ReadonlyMap<Id, FacetOf<Row, Value>>
  search?: (row: Row) => string | undefined
  /** A table's own rule for what the search finds, in place of `search`'s text. */
  matches?: (row: Row, query: string) => boolean
  filters: Partial<Record<Id, readonly string[]>>
  query: string
}) {
  const sought = query.trim().toLowerCase()
  const chosenIn = (id: Id) => filters[id] ?? []
  const found = (row: Row) =>
    !sought ||
    (matches ? matches(row, query) : (search?.(row) ?? "").toLowerCase().includes(sought))
  const passes = (row: Row, except?: Id) =>
    found(row) &&
    [...facets].every(([id, facet]) => {
      const chosen = chosenIn(id)
      return (
        id === except ||
        chosen.length === 0 ||
        facet(row).some(({ value }) => chosen.includes(value))
      )
    })

  const counts = new Map(
    [...facets].map(([id, facet]) => {
      const values = new Map(rows.flatMap(facet).map((value) => [value.value, value]))
      // Each row once per value it carries, however many times its facet names it.
      const carried = rows
        .filter((row) => passes(row, id))
        .flatMap((row) => [...new Set(facet(row).map(({ value }) => value))])
      return [
        id,
        [...values.values()].map((value) => ({
          value,
          count: carried.filter((each) => each === value.value).length,
        })),
      ] as const
    }),
  )

  return {
    matching: rows.filter((row) => passes(row)),
    filtered: sought !== "" || [...facets.keys()].some((id) => chosenIn(id).length > 0),
    counts,
  }
}

/**
 * `rows` with those `pinned` picks first, then the rest, each kept in the order it came in: a
 * table's pinned rows lead whatever it is sorted by. The list passed in is left as it was.
 */
export function pinnedFirst<Row>(rows: readonly Row[], pinned: (row: Row) => boolean) {
  return [...rows.filter(pinned), ...rows.filter((row) => !pinned(row))]
}
